import { motion } from "framer-motion";
import { IonIcon } from "@/components/icons/AppIcon";
import { SUPPORT_EMAIL } from "@/lib/assets";

const STEPS = [
  {
    icon: "person-circle-outline",
    title: "Open the Waidbook app",
    desc: "Launch Waidbook on your device and make sure you are signed in to the account you wish to delete.",
  },
  {
    icon: "settings-outline",
    title: "Go to Profile → Settings",
    desc: "Tap your profile icon in the bottom navigation, then open the Settings menu.",
  },
  {
    icon: "trash-outline",
    title: "Tap “Delete Account”",
    desc: "Scroll to the Account section and select “Delete Account”.",
  },
  {
    icon: "alert-circle-outline",
    title: "Confirm your decision",
    desc: "Confirm the prompt (you may be asked to re-enter your password). Your account and all associated data will be permanently deleted.",
  },
];

export default function DeleteAccount() {
  return (
    <div data-testid="delete-account-page" className="relative bg-[#121311]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_top,rgba(202,163,98,0.10),transparent_60%)]" />

      <div className="relative mx-auto max-w-3xl px-6 pb-28 pt-36 md:pt-44">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#CAA362]">
            Account Management
          </p>
          <h1 className="font-serif-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
            Delete Your Account
          </h1>
          <p className="mt-6 text-lg font-light leading-relaxed text-white/70">
            You can permanently delete your Waidbook account and all of your data
            directly from within the app. Follow the simple steps below.
          </p>
          <div className="hairline mt-8" />
        </motion.div>

        {/* Steps */}
        <div className="mt-12 space-y-4">
          {STEPS.map((s, i) => (
            <motion.div
              key={i}
              data-testid={`delete-step-${i + 1}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex items-start gap-5 rounded-2xl border border-white/5 bg-[#1C1D1A] p-6 md:p-8"
            >
              <div className="icon-tile shrink-0">
                <IonIcon name={s.icon} />
              </div>
              <div>
                <div className="mb-1 flex items-center gap-3">
                  <span className="font-serif-display text-xl font-semibold text-[#E0BE82]">
                    {i + 1}.
                  </span>
                  <h3 className="text-lg font-medium text-white">{s.title}</h3>
                </div>
                <p className="text-sm font-light leading-relaxed text-white/60">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* What gets deleted */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mt-10 rounded-2xl border border-[#CAA362]/20 bg-[#292A26] p-8"
        >
          <h2 className="font-serif-display text-2xl font-semibold text-white">
            What gets deleted
          </h2>
          <p className="mt-3 text-sm font-light leading-relaxed text-white/60">
            When you delete your account, the following data is permanently
            removed and cannot be recovered:
          </p>
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            {[
              "Your profile and login credentials",
              "All hunting logs, harvest records and notes",
              "Saved stands, markers and territories",
              "Uploaded photos and attachments",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#CAA362]" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-white/5 bg-[#121311] p-4 text-sm text-white/60">
            <IonIcon name="time-outline" size={20} className="mt-0.5 text-[#CAA362]" />
            <span>
              Deletion is immediate and irreversible. Please export or back up
              any records you wish to keep before continuing.
            </span>
          </div>
        </motion.div>

        {/* Need help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mt-10 rounded-2xl border border-white/5 bg-[#1C1D1A] p-8 text-center"
        >
          <h2 className="font-serif-display text-2xl font-semibold text-white">
            Can't access the app?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sm font-light leading-relaxed text-white/60">
            If you are unable to sign in to delete your account yourself, email
            us from the address associated with your account and we will process
            your deletion request within 30 days.
          </p>
          <a
            data-testid="delete-account-support-email"
            href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request`}
            className="mt-6 inline-flex items-center gap-3 bg-[#CAA362] px-8 py-4 text-sm font-semibold text-[#121311] transition-colors hover:bg-[#E0BE82]"
          >
            <IonIcon name="mail-outline" size={18} />
            {SUPPORT_EMAIL}
          </a>
        </motion.div>
      </div>
    </div>
  );
}
